// src/features/caas/hooks/useCaasClient.ts
import { useState, useEffect, useCallback } from 'react';
import { CaasClient, CaasTeam, Space } from '../../../types';
import { getClient, listTeams, getClientSpaces } from '../../../services/api/endpoints/caas';
import { useAuth } from '../../../contexts/AuthContext';

interface UseCaasClientResult {
  client: CaasClient | null;
  teams: CaasTeam[];
  spaces: Space[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useCaasClient = (clientId: string | undefined): UseCaasClientResult => {
  const { user } = useAuth();
  const [client, setClient] = useState<CaasClient | null>(null);
  const [teams, setTeams] = useState<CaasTeam[]>([]);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClientData = useCallback(async () => {
    if (!clientId) {
      setError('No client ID provided');
      setIsLoading(false);
      return;
    }

    if (!user?.token) {
      setError('No authentication token available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch client details
      const clientData = await getClient(user.token, clientId);
      setClient(clientData);

      // Fetch all teams and filter for this client
      const allTeams = await listTeams(user.token);
      const clientTeams = allTeams.filter(team => team.caas_client_id === clientId);
      setTeams(clientTeams);

      // Fetch spaces for this client
      try {
        const clientSpaces = await getClientSpaces(user.token, clientId);
        setSpaces(clientSpaces);
      } catch (spacesError) {
        // Spaces might not exist, but don't fail the whole request
        console.warn('Could not fetch spaces for client:', spacesError);
        setSpaces([]);
      }
    } catch (err: any) {
      console.error('Error fetching CaaS client:', err);
      setError(err.message || 'Failed to load CaaS client');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, user?.token]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  return {
    client,
    teams,
    spaces,
    isLoading,
    error,
    refresh: fetchClientData,
  };
};
